import React, {
    useState,
    useCallback,
    useEffect,
} from 'react';
import _get from 'lodash/get';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import Grid from '@mlb/react-patterns/components/Grid/grid.tsx';
import BREAKPOINTS from '@mlb/react-patterns/style/breakpoints.ts';

import LoadingButton from '../LoadingButton';
import { cardContexts } from '../Card';
import Card from '../../containers/Card';
import { pageTemplate } from 'bdvp-common/src/styles/theme';

const createCardFiller = (sizes, orientation, loading) => ({
    ...sizes,
    content: (
        <Card
            orientation={orientation}
            isLoading={loading}
        />
    ),
});

const Container = styled.div``;

// The 'sizes' attribute for img is generated here and passed into the Card component.
// The sizes object should always have at least 'xs' defined.
const buildImagePropSizes = ({ sizes, gutter }) => {

    return [
        'xl',
        'lg',
        'md',
        'sm',
        'xs',
    ].reduce((acc,size,index,array) => {

        let gridSize = sizes[size];
        let currentPadding = pageTemplate.paddingLeftRight[size];

        if (!gridSize && !currentPadding) {

            return acc;

        }

        let gridIndex = index + 1;
        while(!gridSize) {

            gridSize = sizes[array[gridIndex]];
            gridIndex++;

        }

        let paddingIndex = index + 1;
        while(!currentPadding) {

            currentPadding = pageTemplate.paddingLeftRight[array[paddingIndex]];
            paddingIndex++;

        }

        let string = `calc(((100vw - ${(parseFloat(currentPadding) * 2 - gutter)}px) / ${100 / gridSize}) - ${gutter}px)`;

        if (index !== array.length - 1) {

            string = `(min-width: ${BREAKPOINTS[size]}) ${string},`;

        }

        return acc += string;

    },'');

};

const ContentCardsGroup = ({
    basePath,
    list,
    handleItemClick,
    config,
    lang,
    isLoading,
    limit,
    sizes,
    verticalGutter,
    horizontalGutter,
    orientation,
    dictionary,
    loadMoreButtonHandler,
    loadingMore,
    hasMore,
    activeItem,
    cardContext,
    cardFocusIndex,
    renderStats,
    searchParams,
    imgSizes,
    ...otherProps
}) => {

    let gridItems;

    const setCardFocus = useCallback(node => {

        if (node) {

            node.focus();

        }

    }, []);

    const [
        focusIndex,
        setFocusIndex,
    ] = useState(cardFocusIndex);

    useEffect(() => {

        setFocusIndex(cardFocusIndex);

    }, [cardFocusIndex]);

    const imagePropSizes = imgSizes || buildImagePropSizes({
        sizes,
        gutter: horizontalGutter,
    });

    if (isLoading) {

        gridItems = Array(limit)
            .fill(createCardFiller, 0, limit)
            .map(card => card(sizes, orientation, isLoading));

    } else if (list && list.length) {

        gridItems = list.map((item, index) => ({
            ...sizes,
            content: (
                <Card
                    basePath={basePath}
                    mediaPlayback={item}
                    orientation={orientation}
                    onClick={(e) => handleItemClick(e, item, index)}
                    config={config}
                    lang={lang}
                    isActive={activeItem && activeItem.slug === item.slug}
                    {...(index === focusIndex ? { ref: setCardFocus } : {})}
                    dictionary={dictionary}
                    cardContext={cardContext}
                    renderStats={renderStats}
                    searchParams={searchParams && searchParams(index)}
                    imgSizes={imagePropSizes}
                />
            ),
        }));

    }

    const loadMoreClickHandler = () => {

        setFocusIndex(list.length);
        loadMoreButtonHandler();

    };

    return (
        <Container {...otherProps}>
            <Grid
                data={gridItems}
                horizontalGutter={horizontalGutter}
                verticalGutter={verticalGutter}
                hasBorderGutter={false}
            />
            {hasMore && (
                <LoadingButton
                    label={loadingMore ? _get(dictionary, 'loading_text', 'Loading') : _get(dictionary, 'see_more_text', 'See More')}
                    onClick={loadMoreClickHandler}
                    loading={loadingMore}
                />
            )}
        </Container>
    );

};

ContentCardsGroup.propTypes = {
    basePath: PropTypes.string.isRequired,
    list: PropTypes.array,
    handleItemClick : PropTypes.func,
    config: PropTypes.object,
    isLoading: PropTypes.bool,
    limit: PropTypes.number,
    sizes: PropTypes.object,
    verticalGutter: PropTypes.number,
    horizontalGutter: PropTypes.number,
    orientation: PropTypes.string,
    dictionary: PropTypes.object,
    loadMoreButtonHandler: PropTypes.func,
    loadingMore: PropTypes.bool,
    hasMore: PropTypes.bool,
    activeItem: PropTypes.object,
    cardContext: PropTypes.string,
    cardFocusIndex: PropTypes.number,
    renderStats: PropTypes.func,
    searchParams: PropTypes.func,
    imgSizes: PropTypes.string,
};

ContentCardsGroup.defaultProps = {
    list: [],
    handleItemClick : null,
    config: {},
    isLoading: false,
    limit: 20,
    sizes: {
        xs: 100,
        sm: 50,
        md: 33.33,
        lg: 25,
        xl: 20,
    },
    verticalGutter: 25,
    horizontalGutter: 20,
    orientation: 'landscape',
    loadingMore: false,
    hasMore: false,
    activeItem: undefined,
    dictionary: {},
    cardContext: cardContexts.MEDIA,
    cardFocusIndex: null,
    renderStats: undefined,
    searchParams: undefined,
    imgSizes: undefined,
};

export default ContentCardsGroup;
